/**
 * ElevenLabs voices for WhatsApp voice replies.
 *
 * The catalog is ElevenLabs' PREMADE set: the voices `GET /v1/voices` returns
 * with `category: "premade"`. Premade voices are ElevenLabs' own defaults,
 * present on every account and usable on every plan, so the key a deployment
 * runs on — staging's, production's — speaks each of them. A library voice is
 * a voice somebody ADDED to one account, which is why none is offered here.
 *
 * Each entry carries the voice's name as ElevenLabs serves it today: its
 * `name` up to the first " - ", and the rest as `description`. Gender and accent
 * are its `labels`. `voice-model-drift` asks the API for every id below and fails
 * when one is served under a different name, so a label cannot drift from the
 * voice that actually speaks.
 *
 * @see https://elevenlabs.io/docs/api-reference/voices/search
 */

import { z } from "zod";

export const ELEVENLABS_VOICE_IDS = [
  "pNInz6obpgDQGcFmaJgB",
  "Xb7hH8MSUJpSbSDYk0k2",
  "hpp4J3VqNfWAUOO0d1Us",
  "pqHfZKP75CvOlQylNhV4",
  "nPczCjzI2devNBz1zQrb",
  "N2lVS1w4EtoT3dr4eOWO",
  "IKne3meq5aSn9XLyUdCD",
  "iP95p4xoKVk53GoZ742B",
  "onwK4e9ZLuTAKqWW03F9",
  "cjVigY5qzO86Huf0OWal",
  "JBFqnCBsd6RMkjVDRZzb",
  "SOYHLrjzK2X1ezoPC6cr",
  "cgSgspJ2msm6clMCkdW9",
  "FGY2WhTYpPnrIDTdsKH5",
  "TX3LPaxmHKxFdv7VOQHJ",
  "pFZP5JQG7iQjIQuC4Bku",
  "XrExE9yKIg1WjnnlVkGX",
  "SAz9YHcvj6GT2YYXdXww",
  "CwhRBWXzGAHq8TQ4Fs17",
  "EXAVITQu4vr4xnSDxMaL",
  "bIHbv24MWmeRgasZH58o"
] as const;
export const ElevenLabsVoiceIdSchema = z.enum(ELEVENLABS_VOICE_IDS);
export type ElevenLabsVoiceId = z.infer<typeof ElevenLabsVoiceIdSchema>;

export interface ElevenLabsVoice {
  /** The voice's name, as ElevenLabs serves it. */
  name: string;
  /** What ElevenLabs serves after the name. */
  description: string;
  gender: "female" | "male" | "neutral";
  accent: string;
}

/** Keyed on every catalog id, so an id cannot be offered without its label. */
export const ELEVENLABS_VOICES: Record<ElevenLabsVoiceId, ElevenLabsVoice> = {
  pNInz6obpgDQGcFmaJgB: {
    name: "Adam",
    description: "Dominant, Firm",
    gender: "male",
    accent: "american"
  },
  Xb7hH8MSUJpSbSDYk0k2: {
    name: "Alice",
    description: "Clear, Engaging Educator",
    gender: "female",
    accent: "british"
  },
  hpp4J3VqNfWAUOO0d1Us: {
    name: "Bella",
    description: "Professional, Bright, Warm",
    gender: "female",
    accent: "american"
  },
  pqHfZKP75CvOlQylNhV4: {
    name: "Bill",
    description: "Wise, Mature, Balanced",
    gender: "male",
    accent: "american"
  },
  nPczCjzI2devNBz1zQrb: {
    name: "Brian",
    description: "Deep, Resonant and Comforting",
    gender: "male",
    accent: "american"
  },
  N2lVS1w4EtoT3dr4eOWO: {
    name: "Callum",
    description: "Husky Trickster",
    gender: "male",
    accent: "american"
  },
  IKne3meq5aSn9XLyUdCD: {
    name: "Charlie",
    description: "Deep, Confident, Energetic",
    gender: "male",
    accent: "australian"
  },
  iP95p4xoKVk53GoZ742B: {
    name: "Chris",
    description: "Charming, Down-to-Earth",
    gender: "male",
    accent: "american"
  },
  onwK4e9ZLuTAKqWW03F9: {
    name: "Daniel",
    description: "Steady Broadcaster",
    gender: "male",
    accent: "british"
  },
  cjVigY5qzO86Huf0OWal: {
    name: "Eric",
    description: "Smooth, Trustworthy",
    gender: "male",
    accent: "american"
  },
  JBFqnCBsd6RMkjVDRZzb: {
    name: "George",
    description: "Warm, Captivating Storyteller",
    gender: "male",
    accent: "british"
  },
  SOYHLrjzK2X1ezoPC6cr: {
    name: "Harry",
    description: "Fierce Warrior",
    gender: "male",
    accent: "american"
  },
  cgSgspJ2msm6clMCkdW9: {
    name: "Jessica",
    description: "Playful, Bright, Warm",
    gender: "female",
    accent: "american"
  },
  FGY2WhTYpPnrIDTdsKH5: {
    name: "Laura",
    description: "Enthusiast, Quirky Attitude",
    gender: "female",
    accent: "american"
  },
  TX3LPaxmHKxFdv7VOQHJ: {
    name: "Liam",
    description: "Energetic, Social Media Creator",
    gender: "male",
    accent: "american"
  },
  pFZP5JQG7iQjIQuC4Bku: {
    name: "Lily",
    description: "Velvety Actress",
    gender: "female",
    accent: "british"
  },
  XrExE9yKIg1WjnnlVkGX: {
    name: "Matilda",
    description: "Knowledgable, Professional",
    gender: "female",
    accent: "american"
  },
  SAz9YHcvj6GT2YYXdXww: {
    name: "River",
    description: "Relaxed, Neutral, Informative",
    gender: "neutral",
    accent: "american"
  },
  CwhRBWXzGAHq8TQ4Fs17: {
    name: "Roger",
    description: "Laid-Back, Casual, Resonant",
    gender: "male",
    accent: "american"
  },
  EXAVITQu4vr4xnSDxMaL: {
    name: "Sarah",
    description: "Mature, Reassuring, Confident",
    gender: "female",
    accent: "american"
  },
  bIHbv24MWmeRgasZH58o: {
    name: "Will",
    description: "Relaxed Optimist",
    gender: "male",
    accent: "american"
  }
};

/**
 * Voice ids a deployment may STORE but that are not offered: the legacy
 * ElevenLabs premade ids this catalog once listed. ElevenLabs no longer lists
 * them, and asked for one directly it answers with a DIFFERENT voice — a paid
 * library voice for five of them, which a free-plan key is refused with
 * `402 paid_plan_required`, and Adam for the other two.
 *
 * Accepted on read and on write, so a deployment storing one keeps parsing and a
 * save made on any other settings tab does not fail on it. Never spoken: every
 * read site resolves it to its replacement first.
 */
export const LEGACY_ELEVENLABS_VOICE_IDS = [
  "21m00Tcm4TlvDq8ikWAM",
  "AZnzlk1XvdvUeBnXmlld",
  "MF3mGyEYCl7XYWbV9V6O",
  "TxGEqnHWrfWFTfGW9XjX",
  "yoZ06aMxZJJ28mfd3POQ",
  "ErXwobaYiN019PkySvjV",
  "VR6AewLTigWG4xSOukaG"
] as const;
export const LegacyElevenLabsVoiceIdSchema = z.enum(LEGACY_ELEVENLABS_VOICE_IDS);
export type LegacyElevenLabsVoiceId = z.infer<typeof LegacyElevenLabsVoiceIdSchema>;

/**
 * Each legacy id → the premade voice that speaks for it, matched on gender (the
 * legacy name's) and an American accent, then on age. ElevenLabs no longer
 * serves the legacy voices, so their own labels cannot be read back; what it
 * answers each id with today is not a candidate — a paid library voice, or Adam.
 *
 * | legacy id              | was     | ElevenLabs serves it as | speaks as (its labels today)         |
 * |------------------------|---------|-------------------------|--------------------------------------|
 * | 21m00Tcm4TlvDq8ikWAM   | Rachel  | Janet (library)         | Jessica — female, american, young    |
 * | AZnzlk1XvdvUeBnXmlld   | Domi    | Elara (library)         | Laura — female, american, young      |
 * | MF3mGyEYCl7XYWbV9V6O   | Elli    | Peter (library)         | Sarah — female, american, young      |
 * | TxGEqnHWrfWFTfGW9XjX   | Josh    | Craig (library)         | Brian — male, american, middle-aged  |
 * | yoZ06aMxZJJ28mfd3POQ   | Sam     | Riley (library)         | Will — male, american, young         |
 * | ErXwobaYiN019PkySvjV   | Antoni  | Adam (premade)          | Chris — male, american, middle-aged  |
 * | VR6AewLTigWG4xSOukaG   | Arnold  | Adam (premade)          | Bill — male, american, old           |
 */
export const LEGACY_ELEVENLABS_VOICE_REPLACEMENTS: Record<
  LegacyElevenLabsVoiceId,
  ElevenLabsVoiceId
> = {
  "21m00Tcm4TlvDq8ikWAM": "cgSgspJ2msm6clMCkdW9",
  AZnzlk1XvdvUeBnXmlld: "FGY2WhTYpPnrIDTdsKH5",
  MF3mGyEYCl7XYWbV9V6O: "EXAVITQu4vr4xnSDxMaL",
  TxGEqnHWrfWFTfGW9XjX: "nPczCjzI2devNBz1zQrb",
  yoZ06aMxZJJ28mfd3POQ: "bIHbv24MWmeRgasZH58o",
  ErXwobaYiN019PkySvjV: "iP95p4xoKVk53GoZ742B",
  VR6AewLTigWG4xSOukaG: "pqHfZKP75CvOlQylNhV4"
};

/**
 * The voice an ElevenLabs deployment with no stored voice speaks: Rachel's
 * replacement, because Rachel was that default — so an unset deployment and one
 * that stored Rachel speak the same voice.
 */
export const DEFAULT_ELEVENLABS_VOICE_ID: ElevenLabsVoiceId =
  LEGACY_ELEVENLABS_VOICE_REPLACEMENTS["21m00Tcm4TlvDq8ikWAM"];

const isElevenLabsVoiceId = (value: string): value is ElevenLabsVoiceId =>
  (ELEVENLABS_VOICE_IDS as readonly string[]).includes(value);

const isLegacyElevenLabsVoiceId = (value: string): value is LegacyElevenLabsVoiceId =>
  (LEGACY_ELEVENLABS_VOICE_IDS as readonly string[]).includes(value);

/**
 * The catalog voice that speaks for `stored`: itself when offered, its
 * replacement when legacy, and the default for anything else — an absent value,
 * an OpenAI voice name left behind by a provider switch, or an id nobody knows.
 */
export function resolveElevenLabsVoiceId(stored: string | undefined): ElevenLabsVoiceId {
  if (stored === undefined) return DEFAULT_ELEVENLABS_VOICE_ID;
  if (isElevenLabsVoiceId(stored)) return stored;
  if (isLegacyElevenLabsVoiceId(stored)) return LEGACY_ELEVENLABS_VOICE_REPLACEMENTS[stored];
  return DEFAULT_ELEVENLABS_VOICE_ID;
}
